// Arsenal Hub — single maintained release record + approved page copy.
// Source of truth: release-manifest.json (ArsenalHub v3.14.5 handoff, 2026-10-11).
// download.enabled stays false until the distribution review is cleared and
// server-side guild-download authorization exists. Do not flip it to make a
// button work — there is deliberately no public download URL here.

export const ARSENAL_HUB = {
  product: "Arsenal Hub",
  publisher: "Arsenal Forge",
  version: "3.14.5",
  releaseLabel: "Guild release",
  buildDate: "2026-10-11",
  game: "WoW Forever",
  addonInterface: "16001",
  platform: "Windows x64",
  components: [
    { name: "Arsenal Hub", version: "3.14.5" },
    { name: "Arsenal Guide", version: "0.4.16" },
    { name: "Arsenal WoW Sync (optional cloud companion)", version: "3.14.1" },
    { name: "Windows installer", version: "1.2.37" },
  ],
  download: {
    enabled: false,
    distribution: "Private Arsenal guild build",
    friendlyFilename: "ArsenalHubWoW.exe",
    originalFilename: "ArsenalHubWoW-v3.14.5-Arrow-Header-Polish.exe",
    sizeBytes: 5014016,
    sizeDisplay: "≈ 4.78 MiB",
    sha256: "d61659a728d451fd78038941cb455e68406bac5f3a50accdb9c49eda3a1a9c5a",
    codeSigned: false,
    holds: [
      "Private guild enrollment configuration is embedded in this build.",
      "The bundled Guide database has a separate, unresolved redistribution check.",
    ],
  },
  tabs: ["Guild", "Character", "Events", "Raids", "Auctions", "Settings"],
  support: {
    discord: "https://discord.gg/TheArsenal",
    discordDisplay: "Discord.gg/TheArsenal",
  },
};

export const HUB_VIDEO = {
  src: "/arsenalhub/hub-demo-v3145.mp4",
  poster: "/arsenalhub/hub-demo-poster.jpg",
};

export const HUB_SCREENSHOTS = [
  {
    src: "/arsenalhub/shot-guild.webp",
    caption: "Guild — member roster with online report, search and guild statistics from the last observed roster",
  },
  {
    src: "/arsenalhub/shot-character.webp",
    caption: "Character — captured equipment for a shared character, with item level, durability and gear-change history",
  },
  {
    src: "/arsenalhub/shot-events.webp",
    caption: "Events — the event planner: create shared events, collect signups and mark attendance",
  },
  {
    src: "/arsenalhub/shot-raids.webp",
    caption: "Raids — the raid planner with role signups, standby, role caps and manual encounters",
  },
  {
    src: "/arsenalhub/shot-auctions.webp",
    caption: "Auctions — browse and search the open Auction House, with saved prices and shopping lists",
  },
  {
    src: "/arsenalhub/shot-settings.webp",
    caption: "Settings — modules and preferences, with per-module switches that keep their records",
  },
];

export const HUB_GUIDE_SHOTS = [
  {
    src: "/arsenalhub/shot-guide-questlog.webp",
    label: "QUEST LOG",
    caption: "Arsenal Guide — the quest log tracker with per-quest distances and turn-in states",
  },
  {
    src: "/arsenalhub/shot-guide-tracker.webp",
    label: "DIRECTION TRACKER",
    caption: "Arsenal Guide — the compact direction tracker: current step, distance and objectives",
  },
  {
    src: "/arsenalhub/shot-guide-details.webp",
    label: "STEP DETAILS",
    caption: "Arsenal Guide — step details with route controls beside the live quest log",
  },
  {
    src: "/arsenalhub/shot-guide-routes.webp",
    label: "ROUTE PICKER",
    caption: "Arsenal Guide — the route picker: leveling routes, zones and dungeons with step counts",
  },
];

export const HUB_FEATURES = [
  {
    title: "Live guild coordination",
    body: "Create guild events and raid plans, share signups and attendance, and see supported updates without logging out to deliver them. A returning member can catch up from an online guildmate holding the saved records. This requires a compatible addon and a peer with the data; it is not an always-on server-hosted feed.",
  },
  {
    title: "Events and raid planning",
    body: "Schedule with the date/time picker, collect role signups, manage role caps and standby, and record attendance and manual encounter results. Creator-controlled delete actions remove events or raids and share their removal with compatible peers. Closing or cancelling and deleting remain distinct actions.",
  },
  {
    title: "Member profiles and captured gear",
    body: "Share a nickname and link mains and alts through confirmation from both characters. View the selected character's shared equipment capture and available stats. The capture's time matters: this is saved and shared gear, not an always-live armory. Loaded characters may show their actual model; unavailable characters use an approximate preview wearing captured gear.",
  },
  {
    title: "Arsenal Guide included",
    body: "Quest guidance, chapter notifications, a larger direction arrow, distance and direction information, and a compact, resizable tracker styled to match the Hub. Quest and step information is shown when available, and your preferred tracker size and position are kept between sessions.",
  },
  {
    title: "Auctions research",
    body: "Browse by item name, link or ID at the Auction House. Read detailed asking prices, compare unit and stack values, build timestamped price history and organise named personal shopping lists. Historical observations are labelled as such. Buying, bidding, posting and cancelling remain in the game's Auction House in this release.",
  },
  {
    title: "A simple installation with optional backup",
    body: "One Windows installer — members do not need build tools, an extra companion download or manual addon copying. Updates create local recovery copies of Arsenal saved data. Cloud backup is optional and separate from live guild sharing; it depends on game disk saves and the enabled companion, and is not real-time cloud sync.",
  },
];

export const HUB_SHARING_STEPS = [
  {
    step: "01",
    title: "Create a plan",
    body: "Build an event or raid plan inside the Hub — date and time, role signups, caps and standby.",
  },
  {
    step: "02",
    title: "Share it in-game",
    body: "Supported changes travel through WoW's addon-message system to compatible online guild members — no logging out to deliver normal updates.",
  },
  {
    step: "03",
    title: "Guildmates receive the update",
    body: "Online members see supported changes as they arrive. Returning members request saved records from available peers. Optional cloud backup runs separately, after the game writes supported data to disk.",
  },
];

export const HUB_INSTALL_STEPS = [
  { text: "Download the authorized Arsenal Hub Windows installer from this page once access is enabled." },
  { text: "Close WoW completely." },
  { text: "Run the installer and select the correct WoW Forever folder when asked:", copy: "Interface\\AddOns" },
  { text: "Choose whether to enable optional Arsenal cloud backup. It starts off on a new installation; a previously paired and enabled installation normally retains that choice." },
  { text: "Reopen WoW, enable Arsenal Hub and Arsenal Guide as needed, and type:", copy: "/ah" },
];

export const HUB_UPDATE_NOTES = [
  "Install updates over the existing copy — do not delete WTF, SavedVariables, guide progress or existing pairing files.",
  "The installer creates local recovery copies under Documents\\Arsenal WoW Backups. This backup is not an authoritative rollback of every guildmate's newer data.",
];

export const HUB_FIRST_USE = [
  { label: "Gear", detail: "Character → Statistics → Capture & share my gear." },
  { label: "Planning", detail: "Events or Raids in the top navigation." },
  { label: "Auctions", detail: "Open the game's Auction House, then Auctions → Browse & search → select an item → Load prices." },
  { label: "Guide window", detail: "/ag show", copy: "/ag show" },
  { label: "Resize tracker", detail: "/ag unlock, drag its bottom-right corner, then /ag lock.", copy: "/ag unlock" },
  { label: "Restore compact tracker", detail: "/ag arrow compact — this is not a quest-progress reset.", copy: "/ag arrow compact" },
];

export const HUB_FAQS = [
  {
    q: "Do members need Arsenal Management?",
    a: "No. Members install the supplied Hub bundle. Arsenal Management is a separate private administrative application.",
  },
  {
    q: "Do we have to log out for raids and events to sync?",
    a: "No — normal supported updates use in-game addon messages between compatible online guildmates. Logout and game saving are separate from live delivery.",
  },
  {
    q: "Does every member need the addon?",
    a: "Members who want to receive these custom shared records need a compatible Arsenal Hub version. Matching current releases are recommended for the complete feature set; different version numbers do not automatically mean communication is broken.",
  },
  {
    q: "Is cloud backup required?",
    a: "No. It is optional. Native guild sharing does not require the cloud companion to run. Backup needs supported saved data, game disk saves, authorized enrollment and a running enabled companion.",
  },
  {
    q: "Will gear always show the exact current character?",
    a: "No. Gear is a captured snapshot. Loaded characters can use their real model; unavailable characters may display an approximate preview. Check the capture timestamp.",
  },
  {
    q: "Can I buy and sell through Arsenal Hub?",
    a: "Not in this release. Use it for auction browsing, asking-price research, history and shopping lists. Perform trading in the game's Auction House.",
  },
  {
    q: "Is this for every WoW version?",
    a: "This build targets WoW Forever with addon interface 16001. Compatibility with other clients is not promised.",
  },
  {
    q: "What is shared?",
    a: "Supported event and raid records, shared gear and opt-in member profiles are exchanged with guildmates. Auction research and shopping lists are not broadcast through guild sync, and private or officer notes are not shared. Optional cloud backup is an additional, separate data path.",
  },
];

export const HUB_ATTRIBUTION =
  "Arsenal Guide inside the bundle: original ForeverGuide code and assets © 2026 RevoltLive85 & prezus, MIT License. The bundled quest database derives from Questie's Classic database and WoW Forever sources and is excluded from that MIT grant — its redistribution confirmation is still pending, which is one reason this build remains a private guild release. Included third-party library notices ship inside the package. GRM and Auctioneer are not dependencies.";
